// BOARD-RESULTS (docs/design/2026-11-02-board-stages-gates-kpis.md §6): a published card's
// results. Until a social connector writes them, a person adds them here: the window (24 h or
// 72 h after posting), when the post actually went out, the channel and content type, and the
// readings (likes, comments, shares, reach, saves). `POST tasks/{id}/results` is the same body a
// connector will send; re-entering a window corrects its readings. Shown only on a card in a done
// column (Published) while the company's `board_results` flag is on.

import type { OperationInput, OperationOutput } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  toast,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";

export type PostResults = OperationOutput<"tasks.results.get">;
type RecordBody = OperationInput<"tasks.results.record">["body"];
type Window = "24h" | "72h";

export const METRICS = ["likes", "comments", "shares", "reach", "saves"] as const;
type Metric = (typeof METRICS)[number];
const LABEL: Record<Metric, string> = {
  likes: "Likes",
  comments: "Comments",
  shares: "Shares",
  reach: "Reach",
  saves: "Saves",
};

export const resultsKey = (co: string, task: string) =>
  ["company", co, "work", "task", task, "results"] as const;

export const resultsQuery = (api: Api, co: string, task: string) =>
  queryOptions({
    queryKey: resultsKey(co, task),
    queryFn: () => api.client.tasks.results.get({ params: { co, id: task } }),
  });

/** `datetime-local` value (local time) of an ISO instant. */
const localInput = (iso: string) => {
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
const fmt = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : v.toLocaleString();

/** The form's inputs prefilled from a window's readings (blank where none). */
const valuesOf = (prior: PostResults["readings"]["24h"]): Record<Metric, string> =>
  Object.fromEntries(
    METRICS.map((m) => [
      m,
      prior?.[m] === null || prior?.[m] === undefined ? "" : String(prior[m]),
    ]),
  ) as Record<Metric, string>;

/** The window a person most likely enters next: 72 h once 24 h is in. */
const nextWindow = (r: PostResults | undefined): Window =>
  r?.readings["24h"] && !r.readings["72h"] ? "72h" : "24h";

export function PostResultsSection({
  api,
  co,
  taskId,
  published,
  canEdit,
}: {
  api: Api;
  co: string;
  taskId: string;
  /** The card is in a done column (Published). */
  published: boolean;
  canEdit: boolean;
}) {
  const q = useQuery({ ...resultsQuery(api, co, taskId), enabled: published, retry: false });
  const [open, setOpen] = useState(false);
  const r = q.data;
  if (!published || !r?.enabled) return null;
  const rows = (["24h", "72h"] as const).map((w) => ({ w, v: r.readings[w] }));
  return (
    <section data-slot="post-results" aria-label="Results" className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h3 className="flex-1 text-sm font-medium">Results</h3>
        {canEdit ? (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setOpen(true)}>
            <PlusIcon aria-hidden="true" />
            Add results
          </Button>
        ) : null}
      </div>
      {r.publication && r.publication.source !== "estimated" ? (
        <p className="text-xs text-muted-foreground">
          Posted {new Date(r.publication.posted_at).toLocaleString()}
          {r.publication.channel ? ` on ${r.publication.channel}` : ""}
          {r.publication.format ? ` · ${r.publication.format}` : ""}
          {r.publication.source === "connector" ? " · from the connector" : ""}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          No results yet. Add the numbers 24 hours and 72 hours after it went out.
        </p>
      )}
      {rows.some((x) => x.v) ? (
        <table className="w-full text-sm tabular-nums">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="text-left font-normal">After</th>
              {METRICS.map((m) => (
                <th key={m} className="text-right font-normal">
                  {LABEL[m]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ w, v }) => (
              <tr key={w} data-window={w}>
                <td>{w === "24h" ? "24 h" : "72 h"}</td>
                {METRICS.map((m) => (
                  <td key={m} className="text-right">
                    {fmt(v?.[m])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open ? (
        <AddResultsDialog
          api={api}
          co={co}
          taskId={taskId}
          results={r}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </section>
  );
}

export function AddResultsDialog({
  api,
  co,
  taskId,
  results,
  onClose,
}: {
  api: Api;
  co: string;
  taskId: string;
  results: PostResults;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const id = useId();
  const pub = results.publication;
  const [win, setWin] = useState<Window>(nextWindow(results));
  const [postedAt, setPostedAt] = useState(pub ? localInput(pub.posted_at) : "");
  const [channel, setChannel] = useState(pub?.channel ?? results.channels[0]?.key ?? "");
  const [format, setFormat] = useState(pub?.format ?? "");
  const [link, setLink] = useState(pub?.post_link ?? "");
  const [values, setValues] = useState(() => valuesOf(results.readings[nextWindow(results)]));
  const numbers = METRICS.filter((m) => values[m].trim() !== "");
  const bad = numbers.some((m) => !/^\d+$/.test(values[m].trim()));
  const valid = postedAt !== "" && /^[a-z][a-z0-9_]*$/.test(channel) && numbers.length > 0 && !bad;
  const save = useMutation({
    mutationFn: () => {
      const body: RecordBody = {
        window: win,
        channel,
        posted_at: new Date(postedAt).toISOString(),
        ...(format ? { format } : {}),
        ...(link.trim() ? { post_link: link.trim() } : {}),
        ...Object.fromEntries(numbers.map((m) => [m, Number(values[m])])),
      } as RecordBody;
      return api.client.tasks.results.record({ params: { co, id: taskId }, body });
    },
    onSuccess: (next) => {
      qc.setQueryData(resultsKey(co, taskId), next);
      toast.add({ title: `${win === "24h" ? "24 h" : "72 h"} results added` });
      onClose();
    },
    onError: (e) => toast.add({ title: "Could not add the results", description: String(e) }),
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: ["company", co, "work", "task", taskId] }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form
          aria-label="Add results"
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !save.isPending) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Add results</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor={`${id}-window`}>After</FieldLabel>
              <NativeSelect
                id={`${id}-window`}
                value={win}
                onChange={(e) => {
                  const next = e.target.value as Window;
                  setWin(next);
                  setValues(valuesOf(results.readings[next]));
                }}
              >
                <NativeSelectOption value="24h">24 hours</NativeSelectOption>
                <NativeSelectOption value="72h">72 hours</NativeSelectOption>
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-posted`}>Posted at</FieldLabel>
              <Input
                id={`${id}-posted`}
                type="datetime-local"
                value={postedAt}
                onChange={(e) => setPostedAt(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-channel`}>Channel</FieldLabel>
              {results.channels.length > 0 ? (
                <NativeSelect
                  id={`${id}-channel`}
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  {results.channels.map((c) => (
                    <NativeSelectOption key={c.key} value={c.key}>
                      {c.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              ) : (
                <Input
                  id={`${id}-channel`}
                  value={channel}
                  placeholder="instagram"
                  onChange={(e) => setChannel(e.target.value.toLowerCase())}
                />
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-format`}>Content type</FieldLabel>
              <NativeSelect
                id={`${id}-format`}
                value={format}
                onChange={(e) => setFormat(e.target.value)}
              >
                <NativeSelectOption value="">Not set</NativeSelectOption>
                {results.formats.map((f) => (
                  <NativeSelectOption key={f.key} value={f.key}>
                    {f.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
            {METRICS.map((m) => (
              <Field key={m}>
                <FieldLabel htmlFor={`${id}-${m}`}>{LABEL[m]}</FieldLabel>
                <Input
                  id={`${id}-${m}`}
                  inputMode="numeric"
                  value={values[m]}
                  aria-invalid={values[m].trim() !== "" && !/^\d+$/.test(values[m].trim())}
                  onChange={(e) => setValues((v) => ({ ...v, [m]: e.target.value }))}
                />
              </Field>
            ))}
          </div>
          <Field>
            <FieldLabel htmlFor={`${id}-link`}>Post link (optional)</FieldLabel>
            <Input
              id={`${id}-link`}
              type="url"
              value={link}
              placeholder="https://"
              onChange={(e) => setLink(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              Save results
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
